import { Box, Icon, Menu, Text } from '@zillow/constellation-10';
import {
  IconChevronDownOutline,
  IconExternalOutline,
  IconLocationOutline,
  IconMinusOutline,
  IconSparkleFilled,
  IconSparkleOutline,
} from '@zillow/constellation-icons';

import { PlacementSource } from '@zg-rentals/ts-rental-manager-api-client';

import { chipLabelStyle, chipRecipe } from './SpotlightControlCell.styles';

import { useAutoSpotlight } from './AutoSpotlightContext';
import { SPOTLIGHT_COPY as COPY } from './spotlightCopy';

import type { ComponentType, MouseEvent } from 'react';

const swallow = (event: MouseEvent) => {
  event.preventDefault();
  event.stopPropagation();
};

type Action = {
  critical?: boolean;
  desc: string;
  icon: ComponentType;
  label: string;
  onSelect: () => void;
};

export function SpotlightControlCell({
  listingAlias,
  placementSource,
  isSpotlighted,
  packageName: packageNameFromApi,
  spotlightEligible,
  managedByFeed,
}: {
  listingAlias: string;
  placementSource?: PlacementSource;
  isSpotlighted?: boolean;
  packageName?: string;
  spotlightEligible?: boolean;
  managedByFeed?: boolean;
}) {
  const ctx = useAutoSpotlight();
  if (!ctx) {
    return null;
  }

  const {
    autoCount,
    availableCount,
    exclude,
    excludedIds,
    feedManagedIds,
    licenses,
    packageNameById,
    pin,
    pinnedCount,
    pinnedIds,
    unexclude,
    unpin,
    vacantIds,
  } = ctx;
  // Feed-managed is per-listing and comes from the advertising endpoint's own gate (RECE-1510/1692) when
  // available; the context set is a Storybook/loading fallback only, not a second source of truth.
  const feedManaged = managedByFeed ?? feedManagedIds.has(listingAlias);

  // placementSource from the advertising API is the source of truth; session context state covers loading.
  const pinned =
    placementSource !== undefined ? placementSource === PlacementSource.PINNED : pinnedIds.has(listingAlias);
  const auto = placementSource === PlacementSource.AUTO;
  const excluded =
    placementSource !== undefined
      ? placementSource === PlacementSource.EXCLUDED
      : !pinned && excludedIds.has(listingAlias);

  // spotlightEligible mirrors whether the advertising endpoint returned listingSpotlight for this row:
  // the backend only populates it for SF spotlight-eligible listings, so false means package display only.
  // undefined means advertising data isn't available (loading/Storybook) and context state decides.
  const vacant = spotlightEligible ?? vacantIds.has(listingAlias);
  const inSpotlight = pinned || auto;
  // The "Spotlight" tag reflects the backend's spotlighted status (isSpotlighted), OR a pinned placement —
  // pinning locks a license and is spotlighted immediately, and placementSource flips to PINNED
  // optimistically, so the tag shows instantly instead of waiting on the advertising refetch. AUTO alone is
  // only a candidate and, unless the backend reports it spotlighted, shows no tag.
  const shouldDisplaySpotlightTag = Boolean(isSpotlighted) || pinned;
  const eligibleNotSelected = vacant && !feedManaged && !inSpotlight && !excluded;
  const packageName = packageNameFromApi ?? packageNameById[listingAlias] ?? COPY.packageNameFallback;

  const productTag = shouldDisplaySpotlightTag ? (
    <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <Box css={{ display: 'inline-flex', alignItems: 'center', gap: '100' }}>
        <Icon size="sm" css={{ color: '{colors.icon.accent.brand.hero}', flexShrink: 0 }}>
          <IconSparkleFilled />
        </Icon>
        <Text textStyle="body-sm-bold" css={{ color: '{colors.text.accent.brand.hero}' }}>
          {COPY.product.spotlight}
        </Text>
      </Box>
      <Text textStyle="body-xs" css={{ color: '{colors.text.subtle}' }}>
        {packageName}
      </Text>
    </Box>
  ) : (
    <Text textStyle="body-sm" css={{ color: '{colors.text.subtle}' }}>
      {packageName}
    </Text>
  );

  // Non-SF / spotlight-ineligible rows never get the dropdown, a state chip, or spotlight branding —
  // package display only.
  if (spotlightEligible === false) {
    return (
      <Text textStyle="body-sm" css={{ color: '{colors.text.subtle}' }}>
        {packageName}
      </Text>
    );
  }

  // Feed-managed (AppFolio/custom-feed) rows are read-only: no pin/exclude/apply/remove — manage in feed only.
  if (feedManaged) {
    return (
      <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '100' }}>
        {productTag}
        <Box css={chipRecipe.raw({ tone: 'static' })}>
          <Icon size="sm" css={{ color: '{colors.Chip.icon.default}', flexShrink: 0 }}>
            <IconExternalOutline />
          </Icon>
          <Text textStyle="body-xs-bold" css={{ color: '{colors.Chip.label.default}', ...chipLabelStyle }}>
            {COPY.state.manageInFeed}
          </Text>
        </Box>
      </Box>
    );
  }

  if (!vacant) {
    return (
      <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '100' }}>
        {productTag}
        <Box css={chipRecipe.raw({ tone: 'disabled' })}>
          <Text textStyle="body-xs-bold" css={{ color: '{colors.Chip.label.default}', ...chipLabelStyle }}>
            {COPY.state.notEligible}
          </Text>
        </Box>
      </Box>
    );
  }

  // Saturation only picks the menu copy; pin() itself decides between pinning and the reassign modal.
  const allPinned = availableCount === 0 && autoCount === 0 && pinnedCount >= licenses;

  const stateLabel = pinned
    ? COPY.state.pinned
    : auto
      ? COPY.state.spotlighted
      : excluded
        ? COPY.state.excluded
        : COPY.state.eligible;
  // Eligible-but-not-selected reads gray to de-emphasize it vs the active spotlighted/pinned states (06-16 model).
  const stateLabelColor = eligibleNotSelected ? '{colors.text.subtle}' : '{colors.Chip.label.default}';

  const pinAction: Action = {
    desc: allPinned ? COPY.actions.pinWhenFull.desc : COPY.actions.pin.desc,
    icon: IconLocationOutline,
    label: COPY.actions.pin.label,
    onSelect: () => pin(listingAlias),
  };
  const unpinAction: Action = {
    desc: COPY.actions.unpin.desc,
    icon: IconSparkleOutline,
    label: COPY.actions.unpin.label,
    onSelect: () => unpin(listingAlias),
  };
  const excludeAction: Action = {
    critical: true,
    desc: COPY.actions.exclude.desc,
    icon: IconMinusOutline,
    label: COPY.actions.exclude.label,
    onSelect: () => exclude(listingAlias),
  };
  const includeAction: Action = {
    desc: COPY.actions.include.desc,
    icon: IconSparkleOutline,
    label: COPY.actions.include.label,
    onSelect: () => unexclude(listingAlias),
  };
  const actions = pinned
    ? [unpinAction, excludeAction]
    : excluded
      ? [includeAction, pinAction]
      : [pinAction, excludeAction];

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '100' }}>
      {productTag}
      <Menu
        css={{ minWidth: '240px' }}
        trigger={
          <Box
            role="button"
            tabIndex={0}
            aria-label="Change spotlight state"
            data-testid={`advertising-state-${listingAlias}`}
            onClick={swallow}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
              }
            }}
            css={chipRecipe.raw({ tone: 'interactive' })}
          >
            <Text textStyle="body-xs-bold" css={{ color: stateLabelColor, ...chipLabelStyle }}>
              {stateLabel}
            </Text>
            <Icon size="sm" css={{ color: '{colors.Chip.icon.default}', flexShrink: 0 }}>
              <IconChevronDownOutline />
            </Icon>
          </Box>
        }
        content={actions.map(({ icon: ActionIcon, label, desc, onSelect, critical }) => (
          <Menu.Item
            key={label}
            onClick={(event) => {
              swallow(event);
              onSelect();
            }}
          >
            <Menu.ItemIcon
              render={<ActionIcon />}
              css={critical ? { color: '{colors.icon.accent.red.strong}' } : undefined}
            />
            <Menu.ItemLabel css={critical ? { color: '{colors.text.accent.red.strong}' } : undefined}>
              {label}
            </Menu.ItemLabel>
            <Menu.ItemMeta>{desc}</Menu.ItemMeta>
          </Menu.Item>
        ))}
      />
    </Box>
  );
}

export default SpotlightControlCell;
